import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PAGE_FONT } from "../../components/metrics/metricsUi";
import { ProductThumbnail } from "../../components/product-image/ProductPicture";
import { formatCount } from "../../components/metrics/metricsFormat";
import { salesHistoryService } from "../../utils/services/salesHistoryService";
import { warehouseIdsKey } from "../../utils/analyticsWarehouses";
import SalesHistoryChart from "./SalesHistoryChart";
import SalesHistoryDailyAnalysis from "./SalesHistoryDailyAnalysis";
import {
  CHANNEL_BREAKDOWN,
  SKU_BREAKDOWN,
  resolveSegmentOrder,
} from "./salesHistoryBreakdown";

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const formatDateShort = (dayKey) => {
  if (!dayKey) return "—";
  const d = new Date(`${dayKey}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return dayKey;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
};

const Kpi = ({ label, value, ui }) => (
  <Box
    sx={{
      ...ui.card,
      px: 1.75,
      py: 1.4,
      minHeight: 78,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 600,
        color: ui.colors.textSecondary,
        letterSpacing: "0.03em",
        textTransform: "uppercase",
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 18,
        fontWeight: 650,
        color: ui.colors.textPrimary,
        fontVariantNumeric: "tabular-nums",
        lineHeight: 1.2,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const SalesHistoryDetailDialog = ({
  open,
  sku,
  productHint,
  clientId,
  startDate,
  endDate,
  warehouseIds = [],
  warehouseLabel,
  sourceChannels = [],
  sourceOptions = [],
  onClose,
  ui,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  // V2.5 daily analysis. Entirely lazy: nothing here runs until a month bar
  // is clicked, and it never blocks or alters the detail request above.
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState(null);
  const [daily, setDaily] = useState(null);
  // SKU is the default so opening a row behaves exactly as before. Product
  // mode is only requested once the user picks it.
  const [scope, setScope] = useState("sku");
  const [breakdown, setBreakdown] = useState("sku");
  // The V1 table row carries no product id, and adding one would mean touching
  // the V1 list aggregation. The SKU detail response already resolves the
  // parent product, so the id is latched from there — Product mode becomes
  // available once the (already lazy) SKU detail has loaded.
  const [productId, setProductId] = useState(null);
  // Latched like productId: both scopes are the same product, so the picture
  // stays put while a scope switch reloads the detail.
  const [productImages, setProductImages] = useState([]);
  // Array props get a new identity on every parent render, which would retrigger
  // the fetch effects forever. Depend on a stable key instead.
  const channelKey = useMemo(
    () => (sourceChannels || []).join("|"),
    [sourceChannels],
  );
  // Sorted, deduplicated warehouse set ("" = All Warehouses) — same stable-key
  // reasoning, and the same selection in any order is one request.
  const warehouseKey = warehouseIdsKey(warehouseIds);
  const requestWarehouseIds = useMemo(
    () => (warehouseKey ? warehouseKey.split(",") : []),
    [warehouseKey],
  );
  // What the request is actually scoped to. In SKU mode this does NOT include
  // productId, so latching the id from the response cannot retrigger a fetch.
  const scopeKey =
    scope === "product" ? `product:${productId || ""}` : `sku:${sku || ""}`;

  // A different row means a fresh modal: back to SKU mode.
  useEffect(() => {
    setScope("sku");
    setBreakdown("sku");
    setProductId(null);
    setProductImages([]);
  }, [sku, open]);

  useEffect(() => {
    if (!open || !clientId || !startDate || !endDate) return undefined;
    if (scope === "product" ? !productId : !sku) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setDetail(null);
      setSelectedMonth(null);
      setDaily(null);
      setDailyError(null);
      try {
        const result = await salesHistoryService.fetchDetail({
          clientId,
          ...(scope === "product" ? { productId } : { sku }),
          startDate,
          endDate,
          warehouseIds: requestWarehouseIds,
          sourceChannels,
        });
        if (!cancelled) {
          setDetail(result);
          if (result?.product_id) setProductId(result.product_id);
          if (result?.product_images) setProductImages(result.product_images);
        }
      } catch (err) {
        if (!cancelled) {
          setDetail(null);
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Unable to load sales history.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scopeKey, clientId, startDate, endDate, warehouseKey, channelKey]);

  useEffect(() => {
    if (!open || !selectedMonth || !clientId) return undefined;
    if (scope === "product" ? !productId : !sku) return undefined;
    let cancelled = false;
    (async () => {
      setDailyLoading(true);
      setDailyError(null);
      setDaily(null);
      try {
        const result = await salesHistoryService.fetchDailyAnalysis({
          clientId,
          ...(scope === "product" ? { productId } : { sku }),
          month: selectedMonth,
          warehouseIds: requestWarehouseIds,
          sourceChannels,
        });
        if (!cancelled) setDaily(result);
      } catch (err) {
        if (!cancelled) {
          setDaily(null);
          setDailyError(
            err?.response?.data?.message ||
              err?.message ||
              "Unable to load daily analysis for this month.",
          );
        }
      } finally {
        if (!cancelled) setDailyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedMonth, scopeKey, clientId, warehouseKey, channelKey]);

  const isProduct = scope === "product";
  const dimension = isProduct && breakdown === "sku"
    ? SKU_BREAKDOWN
    : CHANNEL_BREAKDOWN;
  // Child SKUs order the stack in By SKU mode; the channel filter options do
  // it for By Channel.
  const segmentOptions = useMemo(
    () => (dimension === SKU_BREAKDOWN ? detail?.skus || [] : null),
    [dimension, detail],
  );
  // One colour order for both charts, derived from the full monthly range so
  // the monthly and daily charts — which are on screen together — never give
  // the same channel or SKU two different colours.
  const colorOrder = useMemo(
    () =>
      resolveSegmentOrder(
        detail?.chart?.months || [],
        dimension,
        segmentOptions || sourceOptions,
      ),
    [detail, dimension, segmentOptions, sourceOptions],
  );

  const handleScopeChange = (_event, next) => {
    if (!next || next === scope) return;
    setScope(next);
    setSelectedMonth(null);
    setDaily(null);
    setDailyError(null);
  };

  const handleMonthClick = (month) => {
    setSelectedMonth((current) => (current === month ? null : month));
  };

  const handleCloseDaily = () => {
    setSelectedMonth(null);
    setDaily(null);
    setDailyError(null);
  };

  const productName = detail?.product || productHint || "SKU";
  const scopeLabel = warehouseLabel || "All Warehouses";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "90vh",
          fontFamily: PAGE_FONT,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          px: 3,
          pt: 2.5,
          pb: 2,
          borderBottom: `1px solid ${ui.colors.border}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, minWidth: 0 }}>
          <ProductThumbnail images={productImages} name={productName} ui={ui} size={88} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 700,
                color: ui.colors.textPrimary,
                lineHeight: 1.25,
              }}
            >
              {productName}
            </Typography>
            <Typography sx={{ mt: 0.4, fontSize: 13, color: ui.colors.textSecondary }}>
              {isProduct ? "Product" : `SKU ${sku}`}
              {" · "}
              {scopeLabel}
              {" · "}
              {startDate} – {endDate}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.1 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={scope}
                onChange={handleScopeChange}
                aria-label="Analysis scope"
                sx={{
                  "& .MuiToggleButton-root": {
                    px: 1.5,
                    py: 0.35,
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: "none",
                  },
                }}
              >
                <ToggleButton value="product" disabled={!productId}>
                  Product
                </ToggleButton>
                <ToggleButton value="sku">SKU</ToggleButton>
              </ToggleButtonGroup>
              {isProduct && detail?.sku_count > 0 && (
                <Tooltip title={(detail.skus || []).join(", ")}>
                  <Chip
                    size="small"
                    label={`Includes ${detail.sku_count} SKU${detail.sku_count === 1 ? "" : "s"}`}
                    sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ borderRadius: "10px" }}>
            {error}
          </Alert>
        )}
        {!loading && detail && (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  md: "repeat(5, 1fr)",
                },
                gap: 1.25,
                mb: 2.5,
              }}
            >
              <Kpi
                ui={ui}
                label="Units Sold"
                value={formatCount(detail.kpis?.units_sold)}
              />
              <Kpi
                ui={ui}
                label="Sales $"
                value={formatMoney(detail.kpis?.gross_sales)}
              />
              <Kpi
                ui={ui}
                label="Days With Sales"
                value={formatCount(detail.kpis?.days_with_sales)}
              />
              <Kpi
                ui={ui}
                label="First Sale"
                value={formatDateShort(detail.kpis?.first_sale)}
              />
              <Kpi
                ui={ui}
                label="Last Sale"
                value={formatDateShort(detail.kpis?.last_sale)}
              />
            </Box>

            <Box sx={{ ...ui.card, px: 2, pt: 1.75, pb: 1.5, mb: 2.5 }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: ui.colors.textPrimary,
                  mb: 0.25,
                }}
              >
                Sales History
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: ui.colors.textSecondary, mb: 1.25 }}>
                Monthly units sold, split by{" "}
                {dimension === SKU_BREAKDOWN ? "child SKU" : "sales channel"}.
                Months without evidence are left blank, not shown as zero. Click
                a month for its daily detail.
              </Typography>
              {isProduct && (
                <Box sx={{ mb: 1.25 }}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={breakdown}
                    onChange={(_e, next) => next && setBreakdown(next)}
                    aria-label="Breakdown"
                    sx={{
                      "& .MuiToggleButton-root": {
                        px: 1.5,
                        py: 0.3,
                        fontSize: 11.5,
                        fontWeight: 600,
                        textTransform: "none",
                      },
                    }}
                  >
                    <ToggleButton value="sku">By SKU</ToggleButton>
                    <ToggleButton value="channel">By Channel</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
              <SalesHistoryChart
                months={detail.chart?.months || []}
                onMonthClick={handleMonthClick}
                selectedMonth={selectedMonth}
                sourceOptions={sourceOptions}
                dimension={dimension}
                segmentOptions={segmentOptions}
                colorOrder={colorOrder}
              />
            </Box>

            {selectedMonth && (
              <SalesHistoryDailyAnalysis
                month={selectedMonth}
                loading={dailyLoading}
                error={dailyError}
                analysis={daily}
                sourceOptions={sourceOptions}
                dimension={dimension}
                segmentOptions={segmentOptions}
                colorOrder={colorOrder}
                hideOrders={isProduct}
                onClose={handleCloseDaily}
                ui={ui}
              />
            )}

          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SalesHistoryDetailDialog;
